import React, { useEffect, useState } from 'react'

import {
  useChannelStateContext,
  useTranslationContext,
  AvatarProps,
  Avatar as DefaultAvatar,
  DefaultChannelData,
} from 'stream-chat-react'

import { ActionSheet, ActionSheetButtonStyle } from '@capacitor/action-sheet'

import { CompanyId, GoalId, Role, UserId } from '../../../../services/api.types'
import './CustomChannelHeader.scss'
import { Capacitor } from '@capacitor/core'
import { store } from '../../../../index'
import { isRtl } from '../../../../i18nInit'
import { hasRoleInCompany } from '../../../../common/auth/authSelectors'
import { IGlobalState } from '../../../../Root/store/appReducer'
import { getChatMessagesExport } from '../../../Activity/store/ActivityActions'

export type ChannelHeaderProps = {
  /** UI component to display a user's avatar, defaults to and accepts same props as: [Avatar](https://github.com/GetStream/stream-chat-react/blob/master/src/components/Avatar/Avatar.tsx) */
  Avatar?: React.ComponentType<AvatarProps>
  /** Manually set the image to render, defaults to the Channel image */
  image?: string
  /** Show a little indicator that the Channel is live right now */
  live?: boolean
  /** Set title manually */
  title?: string
  userId: UserId
  goalId: GoalId
  companyId?: CompanyId
}

const UnMemoizedChannelHeader = (props: ChannelHeaderProps) => {
  const { Avatar = DefaultAvatar, image: propImage, live, title, userId, goalId, companyId } = props

  const { channel, watcher_count } = useChannelStateContext('ChannelHeader')
  //const { openMobileNav } = useChatContext<At, Ch, Co, Ev, Me, Re, Us>('ChannelHeader');
  const { t } = useTranslationContext('ChannelHeader')

  const { image: channelImage, /*member_count,*/ name, subtitle } = (channel?.data as DefaultChannelData) || {}

  const image = propImage || channelImage

  const [muted, setMuted] = useState(false)
  const [contextMenuVisible, setContextMenuVisible] = useState(false)

  useEffect(() => {
    setMuted(channel.muteStatus().muted)
  }, [])

  const toggleContextMenu = () => {
    if (Capacitor.getPlatform() === 'web') {
      return setContextMenuVisible(!contextMenuVisible)
    }

    try {
      const showActions = async () => {
        const options = []
        if (muted) {
          options.push({
            title: t('Unmute'),
            style: ActionSheetButtonStyle.Default,
          })
        } else {
          options.push({
            title: t('Mute'),
            style: ActionSheetButtonStyle.Destructive,
          })
        }
        if (isAdmin) {
          options.push({
            title: t`Export`,
            style: ActionSheetButtonStyle.Default,
          })
        }
        options.push({
          title: t`Cancel`,
          style: ActionSheetButtonStyle.Cancel,
        })
        const result = await ActionSheet.showActions({
          title,
          options,
        })

        if (result) {
          if (result.index === 0) {
            if (muted) {
              await channel.unmute({ user_id: userId })
              setMuted(false)
            } else {
              await channel.mute({ user_id: userId })
              setMuted(true)
            }
          }
          if (result.index === 1 && isAdmin) {
            await exportChatMessages()
          }
        }
      }

      showActions()
    } catch (e) {
      return setContextMenuVisible(!contextMenuVisible)
    }
  }

  const toggleMute = async () => {
    await setContextMenuVisible(!contextMenuVisible)
    if (muted) {
      await channel.unmute({ user_id: userId })
      setMuted(false)
    } else {
      await channel.mute({ user_id: userId })
      setMuted(true)
    }
  }

  const exportChatMessages = async () => {
    setContextMenuVisible(false)
    store.dispatch(getChatMessagesExport(goalId))
  }

  const isAdmin = companyId && hasRoleInCompany(store.getState() as any as IGlobalState, companyId, Role.ADMIN)

  return (
    <div className="str-chat__header-livestream">
      {/*<div className='str-chat__header-hamburger' onClick={openMobileNav}>
        <span className='str-chat__header-hamburger--line'></span>
        <span className='str-chat__header-hamburger--line'></span>
        <span className='str-chat__header-hamburger--line'></span>
      </div>*/}
      {image && <Avatar image={image} />}
      <div className="str-chat__header-livestream-left">
        {/*<div className="str-chat__header-livestream-left--title">
          {title || name} {live && <span className="str-chat__header-livestream-left--livelabel">{t('live')}</span>}
        </div>*/}
        {subtitle && <p className="str-chat__header-livestream-left--subtitle">{subtitle}</p>}
        <div className="str-chat__header-livestream-left--members">
          {/*{!live && !!member_count && member_count > 0 && (
            <>
              {t('{{ memberCount }} members', {
                memberCount: member_count,
              })}
              ,{' '}
            </>
          )}*/}
          {/*<a onClick={toggleMute} style={{ opacity: 0.5, float: 'right', cursor: 'pointer' }}>
            {muted ? t('Unmute') : t('Mute')}
          </a>*/}

          {t('{{ watcherCount }} online', { watcherCount: watcher_count })}

          <div
            style={{
              /*float: isRtl ? 'left' : 'right',*/ display: 'inline-block',
              verticalAlign: 'middle',
              cursor: 'pointer',
              marginLeft: isRtl ? 0 : '8px',
              marginRight: isRtl ? '8px' : 0,
            }}
            className="str-chat__actions-button"
            onClick={toggleContextMenu}
          />

          <div className="yum-goal-card-context-menu">
            <ul
              className={`yum-goal-card-context-menu__dropdown ${
                contextMenuVisible ? 'yum-goal-card-context-menu__dropdown--open' : ''
              }`}
            >
              <li>
                <button className="yum-goal-card-context-menu__link" type="button" onClick={toggleMute}>
                  {/*<LogOutIcon className="yum-goal-card-context-menu__icon" />*/}
                  {muted ? t('Unmute') : t('Mute')}
                </button>
              </li>
              {isAdmin && (
                <li>
                  <button className="yum-goal-card-context-menu__link" type="button" onClick={exportChatMessages}>
                    {t`Export`}
                  </button>
                </li>
              )}
            </ul>
            {contextMenuVisible && <div className="yum-goal-card-context-menu__backdrop" onClick={toggleContextMenu} />}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * The ChannelHeader component renders some basic information about a Channel.
 */
export const CustomChannelHeader = React.memo(UnMemoizedChannelHeader) as typeof UnMemoizedChannelHeader
